import React, { useState } from 'react';
import type { DashboardProject } from '../../../types/project';
import { SpecificationDocument } from '../../../types/index';
import { Button } from '../../../../../components/ui/button';
import { Trash2, Download, FileText, Upload, Eye } from 'lucide-react';
import { SPEC_TYPE_LABELS } from '../../../constants/specificationConstants';
import { DocumentUploadModal } from './DocumentUploadModal';
import { DocumentDownloadModal } from './DocumentDownloadModal';
import { DocumentDeleteModal } from './DocumentDeleteModal';
import { SpecificationDetailModal } from './SpecificationDetailModal';
import { FatChecklistSpecificationModal } from './Fat/FatChecklistSpecificationModal';
import { CoatingChecklistSpecificationModal } from './Coating/CoatingChecklistSpecificationModal';

/** Passed from fabrication pipe cards so FAT / coating specs can open full checklist views (Spools). */
export type FatPipeContextForDocuments = {
  spoolSource: unknown;
  showSpoolFatChecklist: boolean;
  /** When true with Spools fabrication, coating modal shows one checklist editor per spool. */
  showSpoolCoatingChecklist?: boolean;
};

interface DocumentGroupsProps {
  specs: SpecificationDocument[];
  onRefresh: (spec: SpecificationDocument) => void;
  handleDeleteDocument: (spec: SpecificationDocument) => void;
  project?: DashboardProject;
  canEdit?: boolean;
  fatPipeContext?: FatPipeContextForDocuments;
  fatTemplateListVersion?: number;
  canReport?: boolean;
}



// Unified styling for all document types — single neutral palette.
// Section identity comes from the heading text, not color.
const ITEM_BG = 'bg-page-bg';
const ICON_COLOR = 'text-heritage-grey';
const BUTTON_COLOR = 'text-blueprint-blue hover:text-hero-red';

const DOCUMENT_TYPES = {
  WPS: { label: SPEC_TYPE_LABELS['WPS'] },
  DRAWING: { label: SPEC_TYPE_LABELS['DRAWING'] },
  WELD_PROCESS: { label: SPEC_TYPE_LABELS['WELD_PROCESS'] },
  INSPECTION_PROCEDURE: { label: SPEC_TYPE_LABELS['INSPECTION_PROCEDURE'] },
  ACCEPTANCE_CRITERIA: { label: SPEC_TYPE_LABELS['ACCEPTANCE_CRITERIA'] },
  PASS_CONFIG: { label: SPEC_TYPE_LABELS['PASS_CONFIG'] },
  FITMENT_READINGS: { label: SPEC_TYPE_LABELS['FITMENT_READINGS'] },
  WELDING_POSITION: { label: SPEC_TYPE_LABELS['WELDING_POSITION'] },
  ALIGNMENT: { label: SPEC_TYPE_LABELS['ALIGNMENT'] },
  OTHERS: { label: SPEC_TYPE_LABELS['OTHERS'] },
  FAT: { label: SPEC_TYPE_LABELS['FAT'] },
  COATING: { label: SPEC_TYPE_LABELS['COATING'] },
} as const;

// Reusable Document Item Component
interface DocumentItemProps {
  spec: SpecificationDocument;
  config: typeof DOCUMENT_TYPES[keyof typeof DOCUMENT_TYPES];
  onRefresh: (spec: SpecificationDocument) => void;
  onDelete: (spec: SpecificationDocument) => void;
  allSpecsOfType?: SpecificationDocument[]; // All specs of the same type
  project?: DashboardProject;
  canEdit?: boolean;
  index?: number;
  fatPipeContext?: FatPipeContextForDocuments;
  fatTemplateListVersion?: number;
  canReport?: boolean;
}

const DocumentItem: React.FC<DocumentItemProps> = ({
  spec,
  config,
  onRefresh,
  onDelete,
  allSpecsOfType = [],
  project,
  canEdit = true,
  index,
  fatPipeContext,
  fatTemplateListVersion = 0,
  canReport = true,
}) => {

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isFatDetailModalOpen, setIsFatDetailModalOpen] = useState(false);
  const [isCoatingDetailModalOpen, setIsCoatingDetailModalOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);




  const handleUpload = (files: FileList | null) => {
    onRefresh(spec);
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Check if there are multiple files of this type
  const hasMultipleFiles = spec?.files?.length > 1;


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleDownload = (spec: any) => {
    if (spec.fileUrl) {
      // Open file URL in a new tab
      window.open(spec.fileUrl, '_blank');
    } else {
    }
  };
  if (!spec?.files) return null;

  const getDetailsText = (spec: any): string => {
    if (!spec) return '';
    switch (spec?.type) {
      case 'WPS':
        return  formatDate(spec?.files?.[0]?.uploadDate);
      case 'DRAWING':
        return  formatDate(spec?.files?.[0]?.uploadDate);
      case 'WELD_PROCESS':
        return `${spec.details?.weldProcess?.processType || ''} - ${spec.details?.weldProcess?.wireType || ''}`;
      case 'INSPECTION_PROCEDURE':
        return spec.details?.inspectionProcedure?.method || '';
      case 'ACCEPTANCE_CRITERIA':
        return spec.details?.acceptanceCriteria?.standard || '';
      case 'PASS_CONFIG':
        return `${spec.details?.passConfig?.numberOfPasses || 0} passes`;
      case 'FITMENT_READINGS':
        const readings = spec.details?.fitmentReadings;
        if (readings) {
          const parts = [];
          if (readings.gapTolerance) parts.push(`Gap: ${readings.gapTolerance}`);
          if (readings.wallThicknessMin && readings.wallThicknessMax) parts.push(`WT: ${readings.wallThicknessMin}-${readings.wallThicknessMax}`);
          if (readings.landMin && readings.landMax) parts.push(`Land: ${readings.landMin}-${readings.landMax}`);
          return parts.length > 0 ? parts.join(', ') : 'Fitment readings available';
        }
        return 'Fitment readings available';
      case 'WELDING_POSITION':
        return `Position: ${spec.details?.weldingPosition?.position || ''}`;
      case 'ALIGNMENT':
        return `${spec?.details?.alignment?.numberOfAxialAlignments || ''} - ${spec.details?.alignment?.alignmentLocation || ''}`;
      case 'OTHERS':
        return  formatDate(spec?.files?.[0]?.uploadDate);
      case 'FAT':
        return  formatDate(spec?.files?.[0]?.uploadDate);
      case 'COATING':
        return spec?.files?.[0]?.uploadDate
          ?  formatDate(spec?.files?.[0]?.uploadDate)
          : 'Coating checklist';
      default:
        return formatDate(spec?.uploadDate);
    }
  };

  const getHeaderText = (spec: any): JSX.Element | string => {
    if (!spec) return '';
    if (lastFile?.fileName && spec?.type !== 'FITMENT_READINGS' && spec?.type !== 'WELD_PROCESS' && spec?.type !== 'PASS_CONFIG' && spec?.type !== 'ALIGNMENT')
      return <p className="text-[13px] font-medium text-text-grey">{lastFile?.fileName}</p>;
    else {
      switch (spec?.type) {
        case 'FITMENT_READINGS':
          return <p className="text-[13px] font-medium text-text-grey">Fitment Readings {index ? index : ''}</p>;
        case 'WELD_PROCESS':
          return <p className="text-[13px] font-medium text-text-grey">Weld Process {index ? index : ''}</p>;
        case 'PASS_CONFIG':
          return <p className="text-[13px] font-medium text-text-grey">Pass Config {index ? index : ''}</p>;
        case 'ALIGNMENT':
          return <p className="text-[13px] font-medium text-text-grey">Alignment {index ? index : ''}</p>;
        case 'COATING':
          return <p className="text-[13px] font-medium text-text-grey">Coating {index ? index : ''}</p>;
        case 'FAT':
          return <p className="text-[13px] font-medium text-text-grey">FAT {index ? index : ''}</p>;
        default:
          return '';
      }
    }
  };


  const lastFile = spec?.files?.[spec?.files?.length - 1];

  return (
    <div>
      <div className={`flex items-center justify-between p-2 ${ITEM_BG} rounded-lg`}>
        <div className="flex items-center gap-3">
          <div className={ICON_COLOR}>
            <FileText className="w-4 h-4" />
          </div>
          <div>
            {getHeaderText(spec)}
            <p className="text-[11px] text-heritage-grey">{getDetailsText(spec)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {spec.type === 'COATING' && (
            <Button
              className={`${BUTTON_COLOR} bg-transparent`}
              onClick={() => setIsCoatingDetailModalOpen(true)}
              title={`View ${config.label}`}
              aria-label={`View ${config.label}`}
              variant="outline"
              size="sm"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
          {spec.type === 'FAT' && (
            <Button
              className={`${BUTTON_COLOR} bg-transparent`}
              onClick={() => setIsFatDetailModalOpen(true)}
              title={`View ${config.label}`}
              aria-label={`View ${config.label}`}
              variant="outline"
              size="sm"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
          {lastFile && (spec.type === 'WELD_PROCESS' || spec.type === 'PASS_CONFIG' || spec.type === 'ALIGNMENT' || spec.type === 'FITMENT_READINGS' || spec.type === 'WPS') && <Button
            className={`${BUTTON_COLOR} bg-transparent`}
            onClick={() => setIsDetailModalOpen(true)}
            title={`View ${config.label} details`}
            aria-label={`View ${config.label} details`}
            variant="outline"
            size="sm"
          >
            <Eye className="h-4 w-4" />
          </Button>}

          {lastFile && canEdit && (
            <Button
              className={`${BUTTON_COLOR} bg-transparent`}
              onClick={() => setIsUploadModalOpen(true)}
              title={`Upload ${config.label}`}
              aria-label={`Upload ${config.label}`}
              variant="outline"
              size="sm"
            >
              <Upload className="h-4 w-4" />
            </Button>
          )}
          {lastFile && lastFile.fileName && (
            <Button
              className={`${BUTTON_COLOR} bg-transparent`}
              onClick={() => {
                if (!canEdit && hasMultipleFiles) return;
                hasMultipleFiles ? setIsDownloadModalOpen(true) : handleDownload(lastFile);
              }}
              disabled={!canEdit && hasMultipleFiles}
              title={hasMultipleFiles ? `View all ${config.label} files` : `Download ${lastFile.fileName}`}
              aria-label={hasMultipleFiles ? `View all ${config.label} files` : `Download ${lastFile.fileName}`}
              variant="outline"
              size="sm"
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
          {canEdit && (
            <Button
              className={`${BUTTON_COLOR} bg-transparent`}
              onClick={() => hasMultipleFiles ? setIsDeleteModalOpen(true) : onDelete(spec)}
              title={hasMultipleFiles ? `Delete specific ${config.label} files` : `Delete ${lastFile?.fileName || config.label}`}
              aria-label={hasMultipleFiles ? `Delete specific ${config.label} files` : `Delete ${lastFile?.fileName || config.label}`}
              variant="outline"
              size="sm"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}

        </div>
      </div>

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
        title={`Upload ${config.label}`}
        acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
        multiple={false}
        maxFiles={1}
        buttonText="Select Document"
        label="Document"
        description="Upload specification documents (PDF, Excel, DOC, etc...)"
        spec={spec}
        documentTypeDisplay={config.label}
      />

      {/* Document Download Modal - Only show when there are multiple files */}
      {hasMultipleFiles && (
        <DocumentDownloadModal
          isOpen={isDownloadModalOpen}
          onClose={() => setIsDownloadModalOpen(false)}
          documents={spec.files}
          onDownload={handleDownload}
          title={`Download ${config.label} Files`}
        />
      )}
      {/* Document Delete Modal - Only show when there are multiple files */}
      {hasMultipleFiles && (
        <DocumentDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          documents={spec.files}
          onDelete={(document) => onDelete({ ...spec, files: [document] })}
          title={`Delete ${config.label} Files`}
        />
      )}

      {/* Specification Detail Modal */}
      {isDetailModalOpen && <SpecificationDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        specification={spec}
        onUpdate={onRefresh}
        pipeDetails={allSpecsOfType}
        project={project}
        canEdit={canEdit}
      />
      }
      {isCoatingDetailModalOpen && spec.type === 'COATING' && (
        <CoatingChecklistSpecificationModal
          isOpen={isCoatingDetailModalOpen}
          onClose={() => setIsCoatingDetailModalOpen(false)}
          specification={spec}
          project={project}
          canEdit={canEdit}
          pipeContext={
            fatPipeContext
              ? {
                  spoolSource: fatPipeContext.spoolSource,
                  showSpoolCoatingChecklist: fatPipeContext.showSpoolCoatingChecklist,
                }
              : undefined
          }
          onPersist={async (next) => {
            await onRefresh(next);
          }}
        />
      )}
      {isFatDetailModalOpen && (
        <FatChecklistSpecificationModal
          isOpen={isFatDetailModalOpen}
          onClose={() => setIsFatDetailModalOpen(false)}
          specification={spec}
          project={project}
          canEdit={canEdit}
          canReport={canReport}
          spoolSource={fatPipeContext?.spoolSource}
          showSpoolFatChecklist={Boolean(fatPipeContext?.showSpoolFatChecklist)}
          fatTemplateListVersion={fatTemplateListVersion}
        />
      )}

      
    </div>
  );
};

// Reusable Document Group Component
interface DocumentGroupProps {
  type: keyof typeof DOCUMENT_TYPES;
  specs: SpecificationDocument[];
  onRefresh: (spec: SpecificationDocument) => void;
  onDelete: (spec: SpecificationDocument) => void;
  project?: DashboardProject;
  canEdit?: boolean;
  fatPipeContext?: FatPipeContextForDocuments;
  fatTemplateListVersion?: number;
  canReport?: boolean;
}

const DocumentGroup: React.FC<DocumentGroupProps> = ({
  type,
  specs,
  onRefresh,
  onDelete,
  project,
  canEdit = true,
  fatPipeContext,
  fatTemplateListVersion,
  canReport,
}) => {

  const config = DOCUMENT_TYPES[type];
  const filteredSpecs = specs.filter(s => s.type === type);

  if (filteredSpecs.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <h5 className="font-heading text-[13px] font-semibold text-blueprint-blue flex items-center gap-2">
        <span className="w-1.5 h-1.5 bg-blueprint-blue rounded-full"></span>
        {config.label}
      </h5>
      <div className="space-y-2">
        {filteredSpecs.map((spec, index) => (
          <DocumentItem
            key={spec.id}
            spec={spec}
            config={config}
            onRefresh={onRefresh}
            onDelete={onDelete}
            allSpecsOfType={filteredSpecs}
            project={project}
            canEdit={canEdit}
            index={index + 1}
            fatPipeContext={fatPipeContext}
            fatTemplateListVersion={fatTemplateListVersion}
            canReport={canReport}
          />
        ))}
      </div>
    </div>
  );
};

export const DocumentGroups: React.FC<DocumentGroupsProps> = ({
  specs,
  onRefresh,
  handleDeleteDocument,
  project,
  canEdit = true,
  fatPipeContext,
  fatTemplateListVersion = 0,
  canReport = true,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {Object.keys(DOCUMENT_TYPES).map((type) => (
        <DocumentGroup
          key={type}
          type={type as keyof typeof DOCUMENT_TYPES}
          specs={specs}
          onRefresh={onRefresh}
          onDelete={handleDeleteDocument}
          project={project}
          canEdit={canEdit}
          fatPipeContext={fatPipeContext}
          fatTemplateListVersion={fatTemplateListVersion}
          canReport={canReport}
        />
      ))}
    </div>
  );
}; 